<template>
  <div class="page-container-v2">
    <a-spin :spinning="loading" tip="加载中...">
      <div class="page-header-wrapper">
        <page-header
          :title="$t('followerlist_header_title')"
          :sub-title="$t('followerlist_header_desc')"
        />
        <a-tooltip :title="$t('followerlist_help_title')">
          <QuestionCircleOutlined class="help-icon" @click="showHelpModal" />
        </a-tooltip>
      </div>

      <div class="card-body px-0 pt-0 pb-2 table-with-search">
        <!-- 검색 필터 영역 -->
        <div class="search-filter-container table-info-filter">
          <div class="search-filter-row">
            <div class="search-filter-title">
              {{ $t('followerlist_search_condition') }}
            </div>
            <div class="search-filter-content">
              <div class="mobile-wrap">
                <a-select 
                  v-model:value="searchFilter" 
                  style="width: 180px"
                  size="small"
                  :placeholder="$t('common_searchfilter_select')"
                >
                  <a-select-option 
                    v-for="option in searchFilterOptions" 
                    :key="option.value" 
                    :value="option.value"
                  >
                    {{ option.label }}
                  </a-select-option>
                </a-select>
                <a-input 
                  size="small"
                  style="width: 400px; margin: 0 8px;"
                  v-model:value="condition"
                  @keyup.enter="enter"
                  :placeholder="getSearchPlaceholder"
                />
                <a-button 
                  size="small"
                  class="black"
                  @click="enter" 
                  style="margin-right: 8px;"
                >
                  {{ $t('common_searchfilter_search') }}
                </a-button>
                <a-button
                  size="small"
                  @click="handleReset"
                >
                  {{ $t('common_searchfilter_reset') }}
                </a-button>
              </div>
            </div>
          </div>
          <div class="search-filter-row">
            <div class="search-filter-title">
              {{ $t('followerlist_follow_date') }}
            </div>
            <div class="search-filter-content">
              <div class="mobile-wrap">
                <a-range-picker
                  v-model:value="dateRange.dates"
                  :format="'YYYY-MM-DD'"
                  :disabled-date="disabledDate"
                  @change="handleDateRangeChange"
                  style="width: 240px;"
                  :locale="antDateLocale"
                  size="small"
                />
              </div>
            </div>
          </div>
          <div class="search-filter-row">
            <div class="search-filter-title">
              {{ $t('followerlist_has_card') }}
            </div>
            <div class="search-filter-content">
              <div class="mobile-wrap">
                <a-select
                  v-model:value="hasCard"
                  style="width: 180px"
                  size="small"
                  @change="handleCardFilterChange"
                >
                  <a-select-option value="ALL">{{ $t('followerlist_all') }}</a-select-option>
                  <a-select-option value="Y">{{ $t('followerlist_yes') }}</a-select-option>
                  <a-select-option value="N">{{ $t('followerlist_no') }}</a-select-option>
                </a-select>
              </div>
            </div>
          </div>
          <!-- 플랫폼 운영자(xinlaian) 전용 gzh(공중계정) 선택 필터 — 제조사 계정에는 미노출 -->
          <div class="search-filter-row" v-if="isXinlaianOperator">
            <div class="search-filter-title">
              {{ $t('followerlist_gzh_filter') }}
            </div>
            <div class="search-filter-content">
              <div class="mobile-wrap">
                <a-select
                  v-model:value="selectedGzh"
                  style="width: 260px"
                  size="small"
                  @change="handleGzhFilterChange"
                >
                  <a-select-option value="ALL">{{ $t('followerlist_all') }}</a-select-option>
                  <a-select-option
                    v-for="option in sortedGzhOptions"
                    :key="option.gzhName"
                    :value="option.gzhName"
                  >
                    {{ formatGzhOptionLabel(option) }}({{ option.count }})
                    <span class="gzh-type-badge">{{ formatGzhTypeLabel(option) }}</span>
                  </a-select-option>
                </a-select>
              </div>
            </div>
          </div>
        </div>

        <!-- 테이블 정보 영역 -->
        <div class="col-xs-12 table-info-filter">
          <div class="total-div">
            <p>
              {{ $t('common_total') }} <span class="number-highlight">{{ formatCommaNumber(listSize) }}</span>{{ $t('followerlist_total_people') }}
            </p>
          </div>
          <div class="search-container">
            <a-select 
              style="width: 150px"
              v-model:value="rowsPerPage" 
              @change="handleRowsPerPage"
              size="small"
            >
              <a-select-option :value="10">{{ $t('common_rows_per_page_10') }}</a-select-option>
              <a-select-option :value="20">{{ $t('common_rows_per_page_20') }}</a-select-option>
              <a-select-option :value="50">{{ $t('common_rows_per_page_50') }}</a-select-option>
              <a-select-option :value="100">{{ $t('common_rows_per_page_100') }}</a-select-option>
            </a-select>
          </div>
        </div>

        <!-- 테이블 영역 -->
        <div v-if="!isMobileView">
          <a-table
            class="ant-table-striped"
            size="small"
            :columns="columns"
            :data-source="responseData"
            :pagination="false"
            :scroll="{ x: 900 }"
            v-if="responseData.length > 0"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'action'">
                <div class="action-buttons">
                  <a-button
                    type="link"
                    size="small"
                    @click="showFollowerDetail(record)"
                  >
                    {{ $t('common_view_detail') }}
                  </a-button>
                  <a-button
                    type="link"
                    size="small"
                    @click="goToCertificateList(record)"
                  >
                    {{ $t('followerlist_view_warranty') }}
                  </a-button>
                </div>
              </template>
            </template>
          </a-table>
          <div class="no-result-container" v-else>
            <i class="fa-solid fa-exclamation-triangle" />
            <span>{{ $t('common_no_result') }}</span>
          </div>
        </div>

        <!-- 모바일 카드뷰 -->
        <div v-else class="mobile-card-list">
          <div v-for="(record, idx) in responseData" :key="record.unionId || idx" class="mobile-company-card">
            <div class="card-field">
              <span class="card-label">{{ $t('followerlist_nickname') }}:</span>
              <span>{{ record.name || record.nickname || '-' }}</span>
            </div>
            <div class="card-field">
              <span class="card-label">{{ $t('followerlist_mobile') }}:</span>
              <span>{{ formatPhoneNumber(record.mobile) }}</span>
            </div>
            <div class="card-field">
              <span class="card-label">{{ $t('followerlist_source') }}:</span>
              <span>{{ record.subscribe_scene || $t('followerlist_unknown_source') }}</span>
            </div>
            <div class="card-field">
              <span class="card-label">{{ $t('followerlist_date') }}:</span>
              <span>{{ formatUnixTimestamp(record.subscribe_time) }}</span>
            </div>
            <div class="card-actions">
              <a-button size="small" @click="showFollowerDetail(record)">
                {{ $t('common_view_detail') }}
              </a-button>
              <a-button size="small" @click="goToCertificateList(record)">
                {{ $t('followerlist_view_warranty') }}
              </a-button>
            </div>
          </div>
          <div v-if="!loading && responseData.length === 0" class="no-result-container">
            <i class="fa-solid fa-exclamation-triangle" />
            <span>{{ $t('common_no_result') }}</span>
          </div>
        </div>
      </div>

      <!-- 페이지네이션 -->
      <pagination 
        v-if="!loading && responseData.length > 0"
        :page-info="paginationData"
        page-name="FollowerList"
        @change-page="paginationHandler"
      />
    </a-spin>

    <!-- 상세 정보 모달 -->
    <a-modal
      v-model:visible="detailModal.visible"
      :title="$t('followerlist_detail_modal_title')"
      :footer="null"
      width="800px"
      class="follower-detail-modal"
    >
      <!-- 상세 표시는 FollowerDetailPanel 로 추출 — Drawer(FollowerLookupDrawer.vue)와 공유 -->
      <FollowerDetailPanel :data="detailModal.data" />
    </a-modal>

    <!-- 도움말 모달 추가 -->
    <a-modal
      v-model:visible="helpModalVisible"
      :title="$t('followerlist_help_title')"
      :footer="null"
      width="600px"
    >
      <div class="help-modal-content">
        <img 
          :src="gongzhonghaoFunnel" 
          :alt="$t('followerlist_help_title')"
          style="width: 100%; height: auto;"
        />
      </div>
    </a-modal>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import dayjs from 'dayjs';
import { oneBtnDialog, formatCommaNumber, isOperator } from '@/utils/utils';
import PageHeader from '@/views/components/PageHeader.vue';
import Pagination from '@/views/components/Pagination.vue';
import followerApis from './api';
import chinaInapi from '@/apis/chinaInapi';
import { QuestionCircleOutlined } from '@ant-design/icons-vue';
import gongzhonghaoFunnel from '@/assets/gongzhonghao_follower_funnel.svg';
import { antDateLocaleMixin } from '@/mixins/antDateLocaleMixin';
import dealerEcountListMixin from '@/mixins/dealerEcountListMixin';
import { GZH_LABEL_MAP, GZH_TYPE_MAP, GZH_ORDER } from './gzh-constants';
import FollowerDetailPanel from './components/FollowerDetailPanel.vue';

export default defineComponent({
  mixins: [antDateLocaleMixin, dealerEcountListMixin],
  name: 'FollowerList',
  components: {
    PageHeader,
    Pagination,
    QuestionCircleOutlined,
    FollowerDetailPanel
  },
  data() {
    return {
      formatCommaNumber,
      apis: null,
      loading: false,
      responseData: [],
      listSize: 0,
      rowsPerPage: 10,
      searchFilter: 'name',
      condition: '',
      searchFilterOptions: [
        { value: 'name', label: this.$t('followerlist_name') },
        { value: 'mobile', label: this.$t('followerlist_phone') },
      ],
      // 진입 시 전체 팔로워를 보여주기 위해 날짜 필터를 기본 비움(P0) — 사용자가
      // 직접 날짜 범위를 선택해야만 필터가 적용된다.
      dateRange: {
        dates: [],
        startDate: undefined,
        endDate: undefined
      },
      maxDateRange: 730,
      paginationData: {
        currentPage: 1,
        totalPages: 0
      },
      columns: [
        {
          title: this.$t('followerlist_nickname'),
          dataIndex: 'name',
          key: 'name',
          align: 'center',
          customRender: ({record}) => record.name || record.nickname || '-'
        },
        {
          title: this.$t('followerlist_mobile'),
          dataIndex: 'mobile',
          key: 'mobile',
          align: 'center',
          customRender: ({text}) => this.formatPhoneNumber(text)
        },
        {
          title: () => (
            <div style="display: flex; align-items: center; justify-content: center; gap: 4px;">
              {this.$t('followerlist_source')}
              <a-tooltip title={this.$t('followerlist_view_wechat_doc')}>
                <i 
                  class="fas fa-info-circle" 
                  style="color: #1890ff; cursor: pointer; font-size: 14px;"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open('https://developers.weixin.qq.com/doc/offiaccount/User_Management/Get_users_basic_information_UnionID.html#%E8%BF%94%E5%9B%9E%E8%AF%B4%E6%98%8E', '_blank');
                  }}
                />
              </a-tooltip>
            </div>
          ),
          dataIndex: 'subscribe_scene',
          key: 'subscribe_scene',
          align: 'center',
          customRender: ({text}) => text || this.$t('followerlist_unknown_source')
        },
        {
          title: this.$t('followerlist_date'),
          dataIndex: 'subscribe_time',
          key: 'subscribe_time',
          align: 'center',
          customRender: ({text}) => this.formatUnixTimestamp(text)
        },
        {
          title: this.$t('common_action'),
          key: 'action',
          align: 'center',
          width: 200
        },
        {
          title: 'OpenID',
          key: 'openIds',
          align: 'center',
          width: 160,
          fixed: 'right',
          customRender: ({record}) => (
            <div style="display: flex; gap: 4px; justify-content: center">
              {record.miniappOpenId && (
                <a-tooltip title={this.$t('followerlist_miniapp_openid')}>
                  <a-button 
                    size="mini"
                    style="font-size: 12px; height: 24px; line-height: 24px;"
                    onClick={() => this.copyToClipboard(record.miniappOpenId, this.$t('followerlist_miniapp_openid'))}
                  >
                    {this.$t('followerlist_miniapp_openid')}
                  </a-button>
                </a-tooltip>
              )}
              {record.gzhOpenIds?.length > 0 && (
                <a-tooltip title={this.$t('followerlist_gzh_openid')}>
                  <a-button 
                    size="mini"
                    style="font-size: 12px; height: 24px; line-height: 24px;"
                    onClick={() => this.copyToClipboard(record.gzhOpenIds[0], this.$t('followerlist_gzh_openid'))}
                  >
                    {this.$t('followerlist_gzh_openid')}
                  </a-button>
                </a-tooltip>
              )}
            </div>
          )
        }
      ],
      // 상세 카드 목록 컬럼(cardColumns)·카드 이미지 매핑은 FollowerDetailPanel.vue 로 이전됨
      detailModal: {
        visible: false,
        data: null
      },
      hasCard: 'ALL',
      gongzhonghaoFunnel,
      helpModalVisible: false,  // 도움말 모달 표시 여부
      // 플랫폼 운영자(xinlaian) 전용 gzh 필터 상태 (SPEC: 팔로워 gzh 필터 FE)
      selectedGzh: 'ALL',
      gzhOptions: [],
    };
  },
  computed: {
    // 운영자 판정은 isOperator() 단일 지점 — 리터럴 비교를 두지 않는다(utils.js)
    isXinlaianOperator() {
      return isOperator();
    },
    getSearchPlaceholder() {
      switch(this.searchFilter) {
        case 'name':
          return this.$t('followerlist_search_placeholder');
        case 'mobile':
          return this.$t('followerlist_search_placeholder_mobile');
        default:
          return this.$t('followerlist_search_placeholder_default');
      }
    },
    // gzhOptions를 GZH_ORDER 순서(信徕安 → 登腾 → 登泰美)로 정렬 — FollowerStats.vue와 동일 규칙.
    // 매핑에 없는 항목은 뒤로 밀린다.
    sortedGzhOptions() {
      return [...this.gzhOptions].sort((a, b) => {
        const ia = GZH_ORDER.indexOf(a.gzhName);
        const ib = GZH_ORDER.indexOf(b.gzhName);
        return (ia === -1 ? GZH_ORDER.length : ia) - (ib === -1 ? GZH_ORDER.length : ib);
      });
    }
  },
  async created() {
    this.apis = followerApis(chinaInapi);
    console.log('Referrer:', document.referrer);
    console.log('컴포넌트 생성 시 hasCard 초기값:', this.hasCard);
  },
  async mounted() {
    if (this.isXinlaianOperator) {
      await this.loadGzhOptions();
    }
    await this.getFollowerList();
  },
  methods: {
    // 날짜 선택 제한 (오늘 이후 날짜 선택 불가)
    disabledDate(current) {
      return current && current > dayjs().endOf('day');
    },
    // gzh(공중계정) 선택 옵션 조회 — 플랫폼 운영자 전용
    async loadGzhOptions() {
      try {
        const response = await this.apis.getGzhOptions();
        if (response.data.success) {
          this.gzhOptions = response.data.data.options || [];
        }
      } catch (error) {
        console.error('Error fetching gzh options:', error);
      }
    },
    // gzh 필터 변경 핸들러
    async handleGzhFilterChange() {
      this.paginationData.currentPage = 1;
      await this.getFollowerList();
    },
    // 검색어 정규화 — 목록은 전화번호를 formatPhoneNumber 로 가공해(189-8402-6862) 보여주지만
    // DB 는 구분자 없는 숫자로 저장한다(운영 실측: wechatusers.mobile 5,881건 전부 구분자 없음).
    // 백엔드가 입력값 그대로 정규식을 걸기 때문에, 화면에 보이는 대로 붙여넣으면 0건이 된다.
    //
    // 주의: 백엔드(/cn/wechat/followers)는 searchFilter 를 받지 않고 nickname·name·mobile 을
    // 항상 OR 로 매칭한다. 즉 검색어 하나가 이름에도 걸리므로 무조건 숫자만 남기면 안 된다
    // ('王春花' → ''). 입력이 "전화번호 모양"(숫자와 전화 구분자로만 구성 + 숫자 3자 이상)일
    // 때만 구분자를 제거하고, 그 외에는 앞뒤 공백만 정리한다.
    normalizeSearchTerm(raw) {
      const trimmed = (raw || '').trim();
      const looksLikePhone = /^[\d\s\-()+.]+$/.test(trimmed) && (trimmed.match(/\d/g) || []).length >= 3;
      return looksLikePhone ? trimmed.replace(/\D/g, '') : trimmed;
    },

    // 팔로워 목록 조회
    async getFollowerList() {
      try {
        this.loading = true;
        const params = {
          page: this.paginationData.currentPage,
          limit: this.rowsPerPage,
          search: this.normalizeSearchTerm(this.condition) || undefined,
          startDate: this.dateRange.startDate,
          endDate: this.dateRange.endDate,
          hasCard: String(this.hasCard),
          gzhName: (this.isXinlaianOperator && this.selectedGzh !== 'ALL') ? this.selectedGzh : undefined
        };

        console.log('API 요청 전 hasCard 값:', this.hasCard);
        console.log('API 요청 파라미터:', params);

        const response = await this.apis.getFollowerList(params);
        if (response.data.success) {
          const { items, paginator } = response.data.data;
          this.responseData = items;
          this.listSize = paginator.totalItems;
          this.paginationData = {
            currentPage: paginator.currentPage,
            totalPages: paginator.totalPages
          };
        }
      } catch (error) {
        console.error('Error fetching follower list:', error);
        await oneBtnDialog(
          this.$t('followerlist_load_err_title'),
          this.$t('followerlist_load_err_content'),
          false
        );
      } finally {
        this.loading = false;
      }
    },
    // Unix timestamp를 날짜 형식으로 변환
    formatUnixTimestamp(timestamp) {
      return dayjs.unix(timestamp).format('YYYY-MM-DD HH:mm:ss');
    },
    // 날짜 범위 변경 핸들러 수정
    async handleDateRangeChange(dates, dateStrings) {
      if (!dates) return;
      
      // 날짜 범위 검증 (2년)
      const [start, end] = dates;
      const diffDays = end.diff(start, 'day');
      if (diffDays > this.maxDateRange) {
        await oneBtnDialog(
          this.$t('followerlist_date_range_exceed'),
          this.$t('followerlist_date_range_limit'),
          false
        );
        return;
      }

      this.dateRange = {
        dates,
        startDate: start.unix(),
        endDate: end.unix()
      };
      this.paginationData.currentPage = 1;
      await this.getFollowerList();
    },
    // 검색 버튼 클릭 또는 엔터 입력 시
    async enter() {
      // 실제로 검색에 쓰인 값을 입력창에도 반영해, 무엇으로 조회됐는지 보이게 한다
      this.condition = this.normalizeSearchTerm(this.condition);
      this.paginationData.currentPage = 1;
      await this.getFollowerList();
    },
    // 페이지당 행 수 변경
    async handleRowsPerPage() {
      this.paginationData.currentPage = 1;
      await this.getFollowerList();
    },
    // 페이지 변경
    async paginationHandler(childPassParam) {
      if (!childPassParam || !childPassParam.page) return;
      this.paginationData.currentPage = childPassParam.page;
      await this.getFollowerList();
    },
    // 검색 조건 초기화
    handleReset() {
      this.searchFilter = 'name';
      this.condition = '';
      this.hasCard = 'ALL';
      this.selectedGzh = 'ALL';
      console.log('초기화 후 hasCard 값:', this.hasCard);
      this.paginationData.currentPage = 1;
      this.getFollowerList();
    },
    // 팔로워 상세 정보 조회
    async showFollowerDetail(record) {
      try {
        this.loading = true;
        const response = await this.apis.getFollowerDetail(record.unionId);
        if (response.data.success) {
          this.detailModal.data = response.data.data;
          this.detailModal.visible = true;
        }
      } catch (error) {
        console.error('Error fetching follower detail:', error);
        await oneBtnDialog(
          this.$t('followerlist_load_err_title'),
          this.$t('followerlist_load_err_content'),
          false
        );
      } finally {
        this.loading = false;
      }
    },
    // 전화번호 포맷팅 함수 — 중국 휴대폰(11자리, 3-4-4)을 기본으로 다룬다.
    // 포맷 조건에 맞지 않으면(자릿수 상이 등) 원문을 그대로 반환한다(포맷 실패 시 원문 노출).
    formatPhoneNumber(phone) {
      if (!phone) return '-';
      const digits = String(phone).replace(/\D/g, '');
      if (digits.length === 11) {
        return digits.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
      }
      return phone;
    },
    // 카드 필터 변경 핸들러
    async handleCardFilterChange(value) {
      console.log('카드 필터 변경 이전 값:', this.hasCard);
      this.hasCard = value;
      console.log('카드 필터 변경 이후 값:', this.hasCard);
      this.paginationData.currentPage = 1;
      await this.getFollowerList();
    },
    // 클립보드 복사 메서드 추가
    async copyToClipboard(text, type) {
      try {
        await navigator.clipboard.writeText(text);
        this.$message.success(this.$t('followerlist_copy_success', { type }));
      } catch (err) {
        console.error('Failed to copy:', err);
        this.$message.error(this.$t('followerlist_copy_fail', { type }));
      }
    },
    // gzh-options의 label을 중국어로 매핑, 없으면 원본 label 폴백 (필터 select 옵션용)
    formatGzhOptionLabel(option) {
      return GZH_LABEL_MAP[option.gzhName] || option.label;
    },
    // 공중계정 유형 배지(订阅号/服务号) — FollowerStats.vue와 동일 규칙
    formatGzhTypeLabel(option) {
      const type = GZH_TYPE_MAP[option.gzhName];
      if (type === 'svc') return this.$t('followerstats_gzh_type_service');
      return this.$t('followerstats_gzh_type_subscribe');
    },
    // 인증서 목록 페이지로 이동
    goToCertificateList(record) {
      const name = record.name || record.nickname;
      if (name) {
        this.$router.push({
          name: 'CertificateList',
          query: {
            searchFilter: 'customerName',
            condition: name
          }
        });
      } else {
        oneBtnDialog(
          this.$t('followerlist_no_name_title'),
          this.$t('followerlist_no_name_content'),
          false
        );
      }
    },
    showHelpModal() {
      this.helpModalVisible = true;
    },
  }
});
</script>

<style lang="scss" scoped>
.card-list-title {
  margin: 20px 0 10px;
  font-weight: bold;
}

.gzh-type-badge {
  font-size: 10px;
  margin-left: 4px;
  color: rgba(0, 0, 0, 0.45);
}

.search-filter-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  background-color: #fafafa;
  border-radius: 4px;
  margin-bottom: 16px;
}

.search-filter-row {
  display: flex;
  align-items: flex-start;
  min-height: 32px;
}

.search-filter-title {
  width: 120px;
  color: rgba(0, 0, 0, 0.85);
  font-weight: bold;
  line-height: 32px;
}

.search-filter-content {
  flex: 1;
  
  .mobile-wrap {
    display: flex;
    align-items: center;
    
    .ant-select {
      .ant-select-selector {
        border-radius: 8px;
      }
    }
    
    .ant-input {
      border-radius: 8px;
      margin: 0 8px;
    }
    
    .ant-btn {
      border-radius: 8px !important;
    }
  }
}

@media screen and (max-width: 767px) {
  .search-filter-row {
    flex-direction: column;
  }
  
  .search-filter-title {
    width: 100%;
    margin-bottom: 8px;
  }
  
  .mobile-wrap {
    flex-direction: column;
    width: 100%;
    
    .ant-select,
    .ant-input {
      width: 100% !important;
      margin: 4px 0 !important;
    }
    
    .ant-btn {
      width: calc(50% - 4px);
      margin: 4px 0 !important;
    }
  }

  .mobile-card-list { display: flex; flex-direction: column; gap: 12px; }
  .mobile-company-card { border: 1px solid #f0f0f0; border-radius: 6px; padding: 12px; background: #fff; }
  .card-field { display: flex; gap: 8px; margin-bottom: 6px; font-size: 13px; align-items: center; }
  .card-label { font-weight: 600; min-width: 80px; flex-shrink: 0; color: #666; }
  .card-actions { display: flex; flex-wrap: wrap; gap: 6px; border-top: 1px solid #f5f5f5; padding-top: 8px; }
  .card-actions :deep(.ant-btn) { flex: 1 1 auto; }
}

// 복사 버튼 스타일 수정
:deep(.ant-btn) {
  padding: 0 6px;
  border-radius: 4px;
  min-width: 60px;
  
  &:hover {
    color: #1890ff;
    border-color: #1890ff;
  }
}

.follower-detail-modal {
  :deep(.ant-modal-content) {
    border-radius: 8px;
    
    .ant-modal-header {
      border-radius: 8px 8px 0 0;
      background-color: #f8f9fa;
      padding: 16px 24px;
      
      .ant-modal-title {
        font-size: 18px;
        font-weight: 600;
      }
    }
    
    .ant-modal-body {
      padding: 0;
    }
  }
}

/* .detail-container 등 상세 표시 스타일은 FollowerDetailPanel.vue 로 이전됨 */

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 8px;
  
  .ant-btn {
    margin: 0;
    
    &:hover {
      color: #1890ff;
      border-color: transparent;
    }
  }
}

.icon-wrapper {
  :deep(.anticon) {
    font-size: 16px;
    color: rgba(0, 0, 0, 0.45);
    cursor: help;
  }
}

.help-button {
  padding: 4px 8px;
  
  :deep(.anticon) {
    font-size: 16px;
    color: rgba(0, 0, 0, 0.45);
  }
  
  &:hover {
    :deep(.anticon) {
      color: #1890ff;
    }
  }
}

// 팝오버 스타일 커스터마이징
:deep(.follower-help-popover) {
  .help-content {
    max-width: 400px;
    text-align: center;
  }
}

.header-title {
  display: flex;
  align-items: center;
  gap: 8px;

  .help-icon {
    font-size: 16px;
    color: rgba(0, 0, 0, 0.45);
    cursor: pointer;
    margin-top: 4px;

    &:hover {
      color: #1890ff;
    }
  }
}

.page-header-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 24px;

  :deep(.ant-page-header-heading-sub-title) {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .help-icon {
    position: relative;
    font-size: 16px;
    color: rgba(0, 0, 0, 0.45);
    cursor: pointer;
    padding: 4px;
    margin-left: 0;

    &:hover {
      color: #1890ff;
    }
  }
}

.help-modal-content {
  padding: 16px;
  text-align: center;
}
</style>
